import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  PlayIcon, 
  TrophyIcon, 
  ChartBarIcon, 
  ClockIcon,
  CurrencyDollarIcon,
  SparklesIcon,
  ArrowPathIcon,
  XMarkIcon,
  StarIcon,
  BookOpenIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  LightBulbIcon,
  NewspaperIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';
import ShareProgressButton from './ShareProgressButton';

const AuraRiskGymGame = ({ user, onClose }) => {
  const [currentStep, setCurrentStep] = useState('story-intro'); // story-intro, story-mission, story-mechanics, game-setup, game-play, achievements
  const [missions, setMissions] = useState([]);
  const [achievements, setAchievements] = useState({ game_xp: 0, earned_badges: [] });
  const [currentSession, setCurrentSession] = useState(null);
  const [currentMission, setCurrentMission] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Game state
  const [portfolioAllocation, setPortfolioAllocation] = useState({});
  const [availableSectors, setAvailableSectors] = useState([]);
  const [newsRecommendations, setNewsRecommendations] = useState([]);
  const [currentYear, setCurrentYear] = useState(0);
  const [gameEvents, setGameEvents] = useState([]);

  // Story state
  const [storyProgress, setStoryProgress] = useState(0);

  useEffect(() => {
    loadMissions();
    loadAchievements();
    initializeSectors();
    loadNewsRecommendations();
  }, []);

  const initializeSectors = () => {
    const sectors = [
      { id: 'technology', name: 'Technology', icon: '💻', risk: 'high', description: 'Tech companies and innovation' },
      { id: 'healthcare', name: 'Healthcare', icon: '🏥', risk: 'medium', description: 'Medical and pharmaceutical companies' },
      { id: 'finance', name: 'Financial Services', icon: '🏦', risk: 'medium', description: 'Banks, insurance, and financial institutions' },
      { id: 'energy', name: 'Energy', icon: '⚡', risk: 'high', description: 'Oil, gas, and renewable energy' },
      { id: 'consumer', name: 'Consumer Goods', icon: '🛒', risk: 'low', description: 'Everyday consumer products' },
      { id: 'real_estate', name: 'Real Estate', icon: '🏠', risk: 'medium', description: 'Property and REIT investments' },
      { id: 'utilities', name: 'Utilities', icon: '🔌', risk: 'low', description: 'Electric, gas, and water companies' },
      { id: 'materials', name: 'Materials', icon: '🏗️', risk: 'high', description: 'Mining, chemicals, and construction' },
      { id: 'communication', name: 'Communication', icon: '📡', risk: 'medium', description: 'Telecom and media companies' },
      { id: 'industrial', name: 'Industrial', icon: '🏭', risk: 'medium', description: 'Manufacturing and aerospace' }
    ];
    setAvailableSectors(sectors);
  };

  const loadNewsRecommendations = async () => {
    try {
      const response = await axios.get('/api/game/sector-recommendations');
      if (response.data.success) {
        setNewsRecommendations(response.data.recommendations);
      }
    } catch (error) {
      // Fallback recommendations
      const recommendations = [
        {
          sector: 'technology',
          recommendation: 'BUY',
          reason: 'AI and cloud computing trends show strong growth potential',
          confidence: 85
        },
        {
          sector: 'healthcare',
          recommendation: 'HOLD',
          reason: 'Stable performance with regulatory factors in play',
          confidence: 70
        },
        {
          sector: 'energy',
          recommendation: 'SELL',
          reason: 'Volatile oil prices and renewable energy transition',
          confidence: 75
        }
      ];
      setNewsRecommendations(recommendations);
    }
  };

  const loadMissions = async () => {
    try {
      const response = await axios.get('/api/game/missions');
      if (response.data && response.data.missions && response.data.missions.length) {
        setMissions(response.data.missions);
      } else {
        // Local default missions fallback
        setMissions([
          { _id: 'mission_1', title: 'Starter Quest', description: 'Learn basics', goal_amount: 20000, starting_capital: 5000, time_limit_years: 3, difficulty: 'beginner', rewards: { xp: 250 } },
          { _id: 'mission_2', title: 'Risk Wrangler', description: 'Tame volatility', goal_amount: 50000, starting_capital: 10000, time_limit_years: 5, difficulty: 'intermediate', rewards: { xp: 500 } }
        ]);
      }
    } catch (error) {
      setMissions([
        { _id: 'mission_1', title: 'Starter Quest', description: 'Learn basics', goal_amount: 20000, starting_capital: 5000, time_limit_years: 3, difficulty: 'beginner', rewards: { xp: 250 } }
      ]);
    }
  };

  const loadAchievements = async () => {
    try {
      const response = await axios.get('/api/user/achievements');
      if (response.data.success) {
        setAchievements(response.data);
      }
    } catch (error) {
    }
  };

  const startMission = async (missionId) => {
    setLoading(true);
    setError(null);
    
    try {
      const mission = missions.find(m => m._id === missionId);
      
      if (!mission) {
        setError('Mission not found');
        setLoading(false);
        return;
      }
      
      
      // Always create a local session for reliability
      const localSession = {
        _id: `local_session_${Date.now()}`,
        current_portfolio: [{ 
          asset_type: 'cash', 
          value: mission.starting_capital || 10000 
        }],
        status: 'active',
        current_year: 0
      };
      
      
      setCurrentSession(localSession);
      setCurrentMission(mission);
      setCurrentYear(0); // Reset year to 0
      setGameEvents([]); // Clear previous events
      
      // Initialize portfolio allocation
      const initialAllocation = {};
      availableSectors.forEach(sector => { 
        initialAllocation[sector.id] = 0; 
      });
      setPortfolioAllocation(initialAllocation);
      
      setCurrentStep('game-setup');
      
      // Try to notify backend (non-blocking)
      try {
        await axios.post('/api/game/start-mission', { mission_id: missionId });
      } catch (e) {
      }
      
    } catch (error) {
      setError('Failed to start mission: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const takeTurn = async () => {
    
    if (!currentMission) {
      setError('❌ No mission selected. Please start a mission first.');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Convert allocation to percentage format
      const allocationArray = Object.entries(portfolioAllocation)
        .filter(([_, percentage]) => percentage > 0)
        .map(([assetType, percentage]) => ({
          asset_type: assetType,
          percentage: percentage
        }));


      // Always use local simulation for reliability
      if (!currentSession || !currentSession._id || currentSession._id === 'mock_session') {
        simulateLocalTurn(allocationArray);
        return;
      }

      let response;
      try {
        response = await axios.post('/api/game/take-turn', {
          session_id: currentSession._id,
          portfolio_allocation: allocationArray
        });
      } catch (e) {
        simulateLocalTurn(allocationArray);
        return;
      }


      if (response.data.success) {
        // Calculate portfolio value change
        const previousValue = currentSession?.current_portfolio?.reduce((sum, asset) => sum + (asset.value || 0), 0) || currentMission?.starting_capital || 0;
        const newPortfolio = response.data.session?.current_portfolio || response.data.session?.portfolio || [];
        const newValue = newPortfolio.reduce((sum, asset) => sum + (asset.value || 0), 0);
        const portfolioChange = previousValue > 0 ? ((newValue - previousValue) / previousValue) * 100 : 0;
        
        const newYear = currentYear + 1;
        
        
        // Update session with new year
        const updatedSession = {
          ...response.data.session,
          current_year: newYear
        };
        
        setCurrentSession(updatedSession);
        setCurrentYear(newYear);
        
        // Generate AI feedback if not provided
        const aiFeedback = response.data.ai_feedback || 
          `Year ${newYear} complete! ${portfolioChange >= 0 ? 'Your portfolio grew' : 'Your portfolio declined'} by ${Math.abs(portfolioChange).toFixed(2)}%. ${
            portfolioChange > 10 ? '🎉 Excellent returns! Your diversification strategy is paying off.' :
            portfolioChange > 5 ? '👍 Solid growth! You\'re on track to reach your goal.' :
            portfolioChange > 0 ? '📈 Steady progress. Adjust your allocation for better returns.' :
            portfolioChange > -5 ? '⚠️ Minor setback. Market volatility is normal - stay the course.' :
            '⚠️ Significant loss. Review your risk tolerance and rebalance.'
          }`;
        
        // Generate market event description if not provided
        const marketEvent = response.data.market_event || {
          description: portfolioChange >= 0 ? '📊 Market Growth Period' : '📉 Market Correction',
          severity: Math.abs(portfolioChange) > 10 ? 'high' : Math.abs(portfolioChange) > 5 ? 'medium' : 'low'
        };
        
        // Add game event with enhanced details
        setGameEvents(prev => [...prev, {
          year: newYear,
          event: marketEvent,
          ai_feedback: aiFeedback,
          portfolioChange: portfolioChange,
          portfolioValue: newValue
        }]);

        // Check if mission is complete
        if (response.data.session.status === 'completed' || newYear >= (currentMission?.time_limit_years || 5)) {
          const completionEvent = {
            year: newYear,
            event: { description: '🎉 Mission Complete!' },
            ai_feedback: `Congratulations! You've completed ${newYear} year${newYear > 1 ? 's' : ''} of investing! Final portfolio value: $${newValue.toLocaleString()}. You've learned valuable lessons about risk management and portfolio diversification that you can apply to your real-life financial visions!`,
            portfolioChange: portfolioChange,
            portfolioValue: newValue
          };
          setGameEvents(prev => [...prev, completionEvent]);
        }
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to take turn');
    } finally {
      setLoading(false);
    }
  };

  const simulateLocalTurn = (allocationArray) => {
    
    // Simulate a turn locally when backend is not available
    const newYear = currentYear + 1;
    
    // Get current portfolio value from session or use starting capital
    const currentValue = currentSession?.current_portfolio?.reduce((sum, asset) => sum + (asset.value || 0), 0) 
      || currentMission?.starting_capital 
      || 10000;
    
    // Calculate new portfolio value based on allocation
    let newValue = 0;
    const newPortfolio = [];
    
    allocationArray.forEach(allocation => {
      const sectorValue = currentValue * (allocation.percentage / 100);
      // Generate random performance for each sector (-10% to +20%)
      const performance = (Math.random() * 30) - 10;
      const newSectorValue = sectorValue * (1 + performance / 100);
      
      newPortfolio.push({
        asset_type: allocation.asset_type,
        value: newSectorValue
      });
      
      newValue += newSectorValue;
    });
    
    const portfolioChange = ((newValue - currentValue) / currentValue) * 100;
    
    
    // Create updated mock session with proper portfolio structure
    const updatedSession = {
      _id: currentSession?._id || 'mock_session',
      current_portfolio: newPortfolio.length > 0 ? newPortfolio : [{ asset_type: 'cash', value: newValue }],
      status: newYear >= (currentMission?.time_limit_years || 5) ? 'completed' : 'active',
      current_year: newYear
    };
    
    // Generate AI feedback
    const aiFeedback = `Year ${newYear} complete! ${portfolioChange >= 0 ? 'Your portfolio grew' : 'Your portfolio declined'} by ${Math.abs(portfolioChange).toFixed(2)}%. ${
      portfolioChange > 10 ? '🎉 Excellent returns! Your diversification strategy is paying off.' :
      portfolioChange > 5 ? '👍 Solid growth! You\'re on track to reach your goal.' :
      portfolioChange > 0 ? '📈 Steady progress. Adjust your allocation for better returns.' :
      portfolioChange > -5 ? '⚠️ Minor setback. Market volatility is normal - stay the course.' :
      '⚠️ Significant loss. Review your risk tolerance and rebalance.'
    }`;
    
    const marketEvent = {
      description: portfolioChange >= 0 ? '📊 Market Growth Period' : '📉 Market Correction',
      severity: Math.abs(portfolioChange) > 10 ? 'high' : Math.abs(portfolioChange) > 5 ? 'medium' : 'low'
    };
    
    // Update state in the correct order
    setCurrentSession(updatedSession);
    setCurrentYear(newYear);
    
    // Add game event
    setGameEvents(prev => [...prev, {
      year: newYear,
      event: marketEvent,
      ai_feedback: aiFeedback,
      portfolioChange: portfolioChange,
      portfolioValue: newValue
    }]);
    
    setLoading(false);
  };

  const handleSectorDrag = (sectorId, percentage) => {
    setPortfolioAllocation(prev => ({
      ...prev,
      [sectorId]: Math.max(0, Math.min(100, percentage))
    }));
  };

  const removeSectorFromPortfolio = (sectorId) => {
    setPortfolioAllocation(prev => ({
      ...prev,
      [sectorId]: 0
    }));
  };

  const distributeEvenly = () => {
    const activeSectors = Object.entries(portfolioAllocation)
      .filter(([_, percentage]) => percentage > 0)
      .map(([sectorId]) => sectorId);
    
    if (activeSectors.length === 0) return;
    
    const evenPercentage = Math.floor(100 / activeSectors.length);
    const remainder = 100 - (evenPercentage * activeSectors.length);
    
    const newAllocation = { ...portfolioAllocation };
    activeSectors.forEach((sectorId, index) => {
      newAllocation[sectorId] = evenPercentage + (index === 0 ? remainder : 0);
    });
    
    setPortfolioAllocation(newAllocation);
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'intermediate': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'advanced': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'low': return 'text-green-400';
      case 'medium': return 'text-yellow-400';
      case 'high': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getRecommendationColor = (recommendation) => {
    switch (recommendation) {
      case 'BUY': return 'text-green-400 bg-green-500/20';
      case 'HOLD': return 'text-yellow-400 bg-yellow-500/20';
      case 'SELL': return 'text-red-400 bg-red-500/20';
      default: return 'text-gray-400 bg-gray-500/20';
    }
  };

  // Story Introduction Component
  const StoryIntro = () => (
    <motion.div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-gray-900 rounded-xl max-w-4xl w-full p-8"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
      >
        <div className="text-center mb-8">
          <div className="w-24 h-24 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full mx-auto mb-6 flex items-center justify-center">
            <TrophyIcon className="w-12 h-12 text-white" />
          </div>
          
          <h2 className="text-4xl font-bold text-white mb-4">
            Welcome to the Financial Risk Gym
          </h2>
          
          <p className="text-xl text-gray-300 mb-4">
            Master the art of portfolio management through interactive storytelling
          </p>
          
          <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 mb-6">
            <p className="text-gray-200 text-base">
              <strong className="text-white">Why practice here?</strong> Before investing real money toward your visions, 
              experiment with different strategies in a risk-free environment. Learn what works, understand market dynamics, 
              and build confidence in your investment decisions. The lessons you learn here will directly help you achieve 
              your real financial goals faster and smarter! 🎯
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white/5 rounded-lg p-6">
            <div className="text-4xl mb-4">📊</div>
            <h3 className="text-xl font-semibold text-white mb-2">Drag & Drop Portfolio</h3>
            <p className="text-gray-400">Allocate funds across different sectors using intuitive drag-and-drop mechanics</p>
          </div>
          
          <div className="bg-white/5 rounded-lg p-6">
            <div className="text-4xl mb-4">📰</div>
            <h3 className="text-xl font-semibold text-white mb-2">News-Driven Decisions</h3>
            <p className="text-gray-400">Make investment choices based on real-time market news and AI recommendations</p>
          </div>
          
          <div className="bg-white/5 rounded-lg p-6">
            <div className="text-4xl mb-4">🎯</div>
            <h3 className="text-xl font-semibold text-white mb-2">Story-Based Learning</h3>
            <p className="text-gray-400">Experience financial concepts through engaging narratives and scenarios</p>
          </div>
        </div>

        <div className="flex justify-center space-x-4">
          <button
            onClick={() => setCurrentStep('story-mission')}
            className="flex items-center px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all duration-200"
          >
            <ArrowRightIcon className="w-5 h-5 mr-2" />
            Begin Your Journey
          </button>
          <button
            onClick={onClose}
            className="px-8 py-4 text-gray-400 hover:text-white transition-colors"
          >
            Maybe Later
          </button>
        </div>
      </motion.div>
    </motion.div>
  );

  // Story Mission Selection Component
  const StoryMissionSelection = () => (
    <motion.div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-gray-900 rounded-xl max-w-6xl w-full h-[80vh] flex flex-col"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
      >
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center">
            <BookOpenIcon className="w-8 h-8 text-purple-400 mr-3" />
            <div>
              <h2 className="text-2xl font-bold text-white">Choose Your Financial Adventure</h2>
              <p className="text-gray-400">Select a mission and begin your story</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setCurrentStep('story-intro')}
              className="flex items-center px-4 py-2 text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              Back
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {missions.map((mission, index) => (
              <motion.div
                key={mission._id}
                className="bg-white/5 border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-all duration-200 cursor-pointer"
                whileHover={{ scale: 1.02 }}
                onClick={() => startMission(mission._id)}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">{mission.title}</h3>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getDifficultyColor(mission.difficulty)}`}>
                    {mission.difficulty}
                  </span>
                </div>
                
                <p className="text-gray-300 mb-4">{mission.description}</p>
                
                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">Goal:</span>
                    <span className="text-white font-medium">${mission.goal_amount.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">Starting Capital:</span>
                    <span className="text-white font-medium">${mission.starting_capital.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">Time Limit:</span>
                    <span className="text-white font-medium">{mission.time_limit_years} years</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">Reward:</span>
                    <span className="text-yellow-400 font-medium">{mission.rewards.xp} XP</span>
                  </div>
                </div>
                
                <button
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-2 rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {loading ? (
                    <ArrowPathIcon className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <PlayIcon className="w-4 h-4 mr-2" />
                      Start Adventure
                    </>
                  )}
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );

  // Story Mechanics Explanation Component
  const StoryMechanics = () => (
    <motion.div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-gray-900 rounded-xl max-w-4xl w-full p-8"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
      >
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-white mb-4">
            How to Play: Your Investment Journey
          </h2>
          <p className="text-gray-300">
            Learn the mechanics through an engaging story-driven experience
          </p>
        </div>

        <div className="space-y-6 mb-8">
          <div className="bg-white/5 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center mr-4">
                <span className="text-2xl">📊</span>
              </div>
              <h3 className="text-xl font-semibold text-white">Step 1: Sector Selection</h3>
            </div>
            <p className="text-gray-300">
              Choose from various sectors like Technology, Healthcare, Energy, and more. Each sector has different risk levels and growth potential.
            </p>
          </div>

          <div className="bg-white/5 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center mr-4">
                <span className="text-2xl">📰</span>
              </div>
              <h3 className="text-xl font-semibold text-white">Step 2: News Analysis</h3>
            </div>
            <p className="text-gray-300">
              Review real-time market news and AI recommendations to make informed decisions about which sectors to invest in.
            </p>
          </div>

          <div className="bg-white/5 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center mr-4">
                <span className="text-2xl">🎯</span>
              </div>
              <h3 className="text-xl font-semibold text-white">Step 3: Portfolio Allocation</h3>
            </div>
            <p className="text-gray-300">
              Drag and drop sectors into your portfolio, allocating percentages based on your risk tolerance and market insights.
            </p>
          </div>

          <div className="bg-white/5 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-yellow-500/20 rounded-lg flex items-center justify-center mr-4">
                <span className="text-2xl">⏰</span>
              </div>
              <h3 className="text-xl font-semibold text-white">Step 4: Time Progression</h3>
            </div>
            <p className="text-gray-300">
              Advance through years and see how your portfolio performs. Learn from market events and adjust your strategy.
            </p>
          </div>
        </div>

        <div className="flex justify-center space-x-4">
          <button
            onClick={() => setCurrentStep('story-mission')}
            className="flex items-center px-6 py-3 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Missions
          </button>
          <button
            onClick={() => setCurrentStep('game-setup')}
            className="flex items-center px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all duration-200"
          >
            Start Playing
            <ArrowRightIcon className="w-5 h-5 ml-2" />
          </button>
        </div>
      </motion.div>
    </motion.div>
  );

  // Game Setup Component
  const GameSetup = () => (
    <motion.div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-gray-900 rounded-xl max-w-7xl w-full h-[90vh] flex flex-col"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
      >
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center">
            <TrophyIcon className="w-8 h-8 text-purple-400 mr-3" />
            <div>
              <h2 className="text-2xl font-bold text-white">{currentMission?.title}</h2>
              <p className="text-gray-400">Set up your initial portfolio allocation</p>
            </div>
          </div>
          <button
            onClick={() => setCurrentStep('story-mechanics')}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 flex">
          {/* Left Panel - Available Sectors */}
          <div className="w-1/3 p-6 border-r border-white/10">
            <h3 className="text-xl font-semibold text-white mb-6">Available Sectors</h3>
            <div className="space-y-3">
              {availableSectors.map((sector) => (
                <div
                  key={sector.id}
                  className="bg-white/5 rounded-lg p-4 cursor-pointer hover:bg-white/10 transition-colors"
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData('sector', JSON.stringify(sector));
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <span className="text-2xl mr-3">{sector.icon}</span>
                      <div>
                        <h4 className="text-white font-medium">{sector.name}</h4>
                        <p className="text-gray-400 text-sm">{sector.description}</p>
                      </div>
                    </div>
                    <span className={`text-sm font-medium ${getRiskColor(sector.risk)}`}>
                      {sector.risk.toUpperCase()} RISK
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Center Panel - Portfolio Allocation */}
          <div className="flex-1 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white">Your Portfolio</h3>
              {Object.values(portfolioAllocation).some(p => p > 0) && (
                <button
                  onClick={distributeEvenly}
                  className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-lg hover:bg-purple-500/30 transition-colors text-sm"
                >
                  Distribute Evenly
                </button>
              )}
            </div>
            <div 
              className="bg-white/5 rounded-lg p-6 min-h-[400px] border-2 border-dashed border-white/20"
              onDrop={(e) => {
                e.preventDefault();
                const sector = JSON.parse(e.dataTransfer.getData('sector'));
                handleSectorDrag(sector.id, 10); // Default 10% allocation
              }}
              onDragOver={(e) => e.preventDefault()}
            >
              <div className="grid grid-cols-2 gap-4">
                {Object.entries(portfolioAllocation)
                  .filter(([_, percentage]) => percentage > 0)
                  .map(([sectorId, percentage]) => {
                    const sector = availableSectors.find(s => s.id === sectorId);
                    return (
                      <div key={sectorId} className="bg-white/10 rounded-lg p-4 relative group">
                        <button
                          onClick={() => removeSectorFromPortfolio(sectorId)}
                          className="absolute top-2 right-2 w-6 h-6 bg-red-500/20 hover:bg-red-500/40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Remove sector"
                        >
                          <XMarkIcon className="w-4 h-4 text-red-300" />
                        </button>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center">
                            <span className="text-xl mr-2">{sector?.icon}</span>
                            <span className="text-white font-medium text-sm">{sector?.name}</span>
                          </div>
                          <span className="text-aurora-teal font-bold">{percentage}%</span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={percentage}
                          onChange={(e) => handleSectorDrag(sectorId, parseInt(e.target.value))}
                          className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                        />
                      </div>
                    );
                  })}
              </div>
              
              {Object.values(portfolioAllocation).every(p => p === 0) && (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">📊</div>
                  <p className="text-gray-400 text-lg">Drag sectors here to build your portfolio</p>
                  <p className="text-gray-500 text-sm">Total allocation must equal 100%</p>
                </div>
              )}
            </div>

            <div className="mt-6 text-center space-y-3">
              <div className="text-lg text-white">
                <span className="text-gray-400">Total Allocation: </span>
                <span className={`font-bold ${
                  Object.values(portfolioAllocation).reduce((sum, val) => sum + val, 0) === 100 
                    ? 'text-green-400' 
                    : 'text-yellow-400'
                }`}>
                  {Object.values(portfolioAllocation).reduce((sum, val) => sum + val, 0)}%
                </span>
                <span className="text-gray-400"> / 100%</span>
              </div>
              
              {Object.values(portfolioAllocation).reduce((sum, val) => sum + val, 0) !== 100 && (
                <p className="text-sm text-yellow-400">
                  {Object.values(portfolioAllocation).reduce((sum, val) => sum + val, 0) < 100 
                    ? `Add ${100 - Object.values(portfolioAllocation).reduce((sum, val) => sum + val, 0)}% more to continue`
                    : `Reduce by ${Object.values(portfolioAllocation).reduce((sum, val) => sum + val, 0) - 100}% to continue`
                  }
                </p>
              )}
              
              <button
                onClick={() => {
                  setCurrentStep('game-play');
                  setCurrentYear(0);
                  setGameEvents([]);
                }}
                disabled={Object.values(portfolioAllocation).reduce((sum, val) => sum + val, 0) !== 100}
                className="px-8 py-3 bg-gradient-to-r from-green-500 to-blue-500 text-white rounded-lg hover:from-green-600 hover:to-blue-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                Start Investment Journey
              </button>
            </div>
          </div>

          {/* Right Panel - News Recommendations */}
          <div className="w-1/3 p-6 border-l border-white/10">
            <h3 className="text-xl font-semibold text-white mb-6 flex items-center">
              <NewspaperIcon className="w-5 h-5 mr-2" />
              Market Insights
            </h3>
            <div className="space-y-4">
              {newsRecommendations.map((rec, index) => {
                const sector = availableSectors.find(s => s.id === rec.sector);
                return (
                  <div key={index} className="bg-white/5 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <span className="text-xl mr-2">{sector?.icon}</span>
                        <span className="text-white font-medium">{sector?.name}</span>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getRecommendationColor(rec.recommendation)}`}>
                        {rec.recommendation}
                      </span>
                    </div>
                    <p className="text-gray-300 text-sm mb-2">{rec.reason}</p>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-400">Confidence:</span>
                      <span className="text-white">{rec.confidence}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );

  // Game Play Component
  const GamePlay = () => (
    <motion.div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-gray-900 rounded-xl max-w-7xl w-full h-[90vh] flex flex-col"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
      >
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center">
            <TrophyIcon className="w-8 h-8 text-purple-400 mr-3" />
            <div>
              <h2 className="text-2xl font-bold text-white">{currentMission?.title}</h2>
              <p className="text-gray-400">Year {currentYear} of {currentMission?.time_limit_years}</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <div className="text-2xl font-bold text-white">
                ${currentSession?.current_portfolio?.reduce((sum, asset) => sum + asset.value, 0)?.toLocaleString() || 0}
              </div>
              <div className="text-sm text-gray-400">Portfolio Value</div>
            </div>
            <button
              onClick={() => setCurrentStep('game-setup')}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex-1 flex overflow-hidden">
          {/* Left Panel - Mission Progress */}
          <div className="w-1/3 p-6 border-r border-white/10 overflow-y-auto">
            <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-400">Progress to Goal</span>
                  <span className="text-sm text-white">
                    {((currentSession?.current_portfolio?.reduce((sum, asset) => sum + asset.value, 0) || 0) / currentMission?.goal_amount * 100).toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-3">
                  <div 
                    className="bg-gradient-to-r from-purple-500 to-pink-500 h-3 rounded-full transition-all duration-500"
                    style={{ 
                      width: `${Math.min(100, ((currentSession?.current_portfolio?.reduce((sum, asset) => sum + asset.value, 0) || 0) / currentMission?.goal_amount * 100))}%` 
                    }}
                  />
                </div>
              </div>

              <div className="bg-white/5 rounded-lg p-4">
                <h3 className="text-white font-semibold mb-2">Mission Goal</h3>
                <p className="text-gray-300 text-sm">{currentMission?.description}</p>
              </div>

              <div className="bg-white/5 rounded-lg p-4">
                <h3 className="text-white font-semibold mb-3">Current Portfolio</h3>
                <div className="space-y-2">
                  {Object.entries(portfolioAllocation)
                    .filter(([_, percentage]) => percentage > 0)
                    .map(([sectorId, percentage]) => {
                      const sector = availableSectors.find(s => s.id === sectorId);
                      const value = (currentSession?.current_portfolio?.reduce((sum, asset) => sum + asset.value, 0) || 0) * (percentage / 100);
                      return (
                        <div key={sectorId} className="flex items-center justify-between text-sm">
                          <span className="text-gray-400 flex items-center">
                            <span className="mr-2">{sector?.icon}</span>
                            {sector?.name}
                          </span>
                          <span className="text-white font-medium">${value.toLocaleString()}</span>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          </div>

          {/* Center Panel - Game Events */}
          <div className="flex-1 p-6 overflow-y-auto">
            <h3 className="text-xl font-semibold text-white mb-4">Investment Journey</h3>
            
            {/* Introductory narrative */}
            {gameEvents.length === 0 && (
              <div className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/30 rounded-lg p-4 mb-6">
                <div className="flex items-center mb-2">
                  <LightBulbIcon className="w-5 h-5 text-yellow-400 mr-2" />
                  <h4 className="text-white font-semibold">How This Helps Your Visions</h4>
                </div>
                <p className="text-gray-300 text-sm mb-2">
                  This Risk Gym helps you understand how different investment strategies affect your goals. By simulating market conditions and seeing real outcomes, you'll:
                </p>
                <ul className="text-gray-300 text-sm space-y-1 ml-4">
                  <li className="flex items-start">
                    <span className="text-purple-400 mr-2">•</span>
                    <span>Learn which sectors align with your risk tolerance</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-400 mr-2">•</span>
                    <span>See how diversification protects your money</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-400 mr-2">•</span>
                    <span>Build confidence in your investment decisions</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-400 mr-2">•</span>
                    <span>Apply these lessons to your real-life visions</span>
                  </li>
                </ul>
              </div>
            )}
            
            <div className="space-y-4">
              {gameEvents.map((event, index) => (
                <div key={index} className="bg-white/5 rounded-lg p-4 border border-white/10 hover:border-purple-500/30 transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <ClockIcon className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-sm font-medium text-white">Year {event.year}</span>
                    </div>
                    {index === gameEvents.length - 1 && (
                      <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded">Latest</span>
                    )}
                  </div>
                  <h4 className="text-white font-medium mb-2">📊 {event.event?.description || 'Market Update'}</h4>
                  <p className="text-gray-300 text-sm mb-3">{event.ai_feedback || 'Your portfolio continues to evolve based on market conditions.'}</p>
                  
                  {/* Show portfolio performance */}
                  {event.portfolioChange && (
                    <div className={`text-sm font-medium ${event.portfolioChange > 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {event.portfolioChange > 0 ? '📈' : '📉'} Portfolio Value: {event.portfolioChange > 0 ? '+' : ''}{event.portfolioChange.toFixed(2)}%
                    </div>
                  )}
                </div>
              ))}
              
              {gameEvents.length === 0 && (
                <div className="text-center py-8">
                  <div className="text-6xl mb-4">🎯</div>
                  <p className="text-gray-400 text-lg font-medium">Ready to start your journey?</p>
                  <p className="text-gray-500 text-sm mt-2">Click "Advance to Year 1" below to see how your portfolio performs!</p>
                </div>
              )}
            </div>

            <div className="mt-8 space-y-3">
              {error && (
                <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 text-red-200 text-sm">
                  {error}
                </div>
              )}
              
              {/* Show completion message and actions */}
              {(currentSession?.status === 'completed' || currentYear >= (currentMission?.time_limit_years || 5)) ? (
                <div className="space-y-3">
                  <div className="bg-gradient-to-r from-green-500/20 to-blue-500/20 border border-green-500/50 rounded-lg p-4">
                    <div className="flex items-center justify-center mb-3">
                      <TrophyIcon className="w-8 h-8 text-yellow-400 mr-2" />
                      <h3 className="text-xl font-bold text-white">Mission Complete!</h3>
                    </div>
                    <p className="text-center text-white/90 mb-2">
                      Congratulations! You've completed all {currentYear} years of this investment journey.
                    </p>
                    <p className="text-center text-white/70 text-sm">
                      Final Portfolio Value: <span className="font-bold text-green-400">
                        ${(currentSession?.current_portfolio?.reduce((sum, asset) => sum + (asset.value || 0), 0) || 0).toLocaleString()}
                      </span>
                    </p>
                    
                    {/* Share Progress */}
                    {currentMission?.mission_id && user && (
                      <div className="mt-4 flex justify-center">
                        <ShareProgressButton
                          entityType="game"
                          entityId={currentMission.mission_id}
                          entityData={{
                            mission_id: currentMission.mission_id,
                            achievement: "Mission Complete",
                            xp_earned: achievements?.game_xp || 0
                          }}
                          user={user}
                        />
                      </div>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => {
                        // Reset and return to mission selection
                        setCurrentStep('story-mission');
                        setCurrentSession(null);
                        setCurrentMission(null);
                        setCurrentYear(0);
                        setGameEvents([]);
                        setPortfolioAllocation({});
                      }}
                      className="flex items-center justify-center px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all duration-200 font-medium"
                    >
                      <ArrowLeftIcon className="w-5 h-5 mr-2" />
                      Choose New Mission
                    </button>
                    
                    <button
                      onClick={() => {
                        // Restart the same mission
                        startMission(currentMission._id);
                      }}
                      className="flex items-center justify-center px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-lg transition-all duration-200 font-medium"
                    >
                      <ArrowPathIcon className="w-5 h-5 mr-2" />
                      Play Again
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={takeTurn}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-green-500 to-blue-500 text-white px-6 py-3 rounded-lg hover:from-green-600 hover:to-blue-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-medium"
                >
                  {loading ? (
                    <ArrowPathIcon className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <ClockIcon className="w-5 h-5 mr-2" />
                      Advance to Year {currentYear + 1}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Right Panel - AI Insights */}
          <div className="w-1/3 p-6 border-l border-white/10 overflow-y-auto">
            <h3 className="text-xl font-semibold text-white mb-6 flex items-center">
              <SparklesIcon className="w-5 h-5 mr-2" />
              AI Insights
            </h3>
            
            <div className="space-y-4">
              <div className="bg-white/5 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <LightBulbIcon className="w-5 h-5 text-yellow-400 mr-2" />
                  <span className="text-white font-medium">Portfolio Analysis</span>
                </div>
                <p className="text-gray-300 text-sm">
                  Your current allocation shows good diversification. Monitor tech sector volatility.
                </p>
              </div>

              <div className="bg-white/5 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <NewspaperIcon className="w-5 h-5 text-blue-400 mr-2" />
                  <span className="text-white font-medium">Market Outlook</span>
                </div>
                <p className="text-gray-300 text-sm">
                  Current market conditions favor defensive sectors. Healthcare and utilities show strong fundamentals.
                </p>
              </div>

              <div className="bg-white/5 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <ChartBarIcon className="w-5 h-5 text-green-400 mr-2" />
                  <span className="text-white font-medium">Risk Assessment</span>
                </div>
                <p className="text-gray-300 text-sm">
                  Your portfolio risk level is moderate. Reduce exposure to high-volatility sectors if market conditions worsen.
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );

  // Render based on current step
  const renderCurrentStep = () => {
    
    switch (currentStep) {
      case 'story-intro':
        return <StoryIntro />;
      case 'story-mission':
        return <StoryMissionSelection />;
      case 'story-mechanics':
        return <StoryMechanics />;
      case 'game-setup':
        return <GameSetup />;
      case 'game-play':
        return <GamePlay />;
      default:
        return <StoryIntro />;
    }
  };

  return (
    <AnimatePresence mode="wait">
      {renderCurrentStep()}
    </AnimatePresence>
  );
};

export default AuraRiskGymGame;